/* 147j+16dd — Swing Trading tab.
 *
 * Three-section layout:
 *   1. Macro Regime banner (RISK_ON / NEUTRAL / RISK_OFF + narrative)
 *   2. Swing Universe (100-name watchlist, bucket-tagged, sortable)
 *   3. Recent Setup Fires (BREAKOUT / PULLBACK / VCP with confidence)
 *
 * Zero LLM cost — all reads. Rebuild + scan actions are admin-only.
 */
/* eslint-disable react/no-unescaped-entities */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, TrendingUp, TrendingDown, Minus, Target, Activity,
  Zap, ChevronRight, ChevronDown, Info, BookOpen,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const BUCKET_TONE = {
  CORE:     'bg-sky-500/15 text-sky-300 border-sky-500/40',
  MOMENTUM: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  SECTOR:   'bg-amber-500/15 text-amber-300 border-amber-500/40',
  EVENT:    'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40',
  FILLER:   'bg-zinc-500/15 text-zinc-400 border-zinc-500/40',
};

const SETUP_TONE = {
  BREAKOUT: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  PULLBACK: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
  VCP:      'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40',
};

const regimeTone = (label) => {
  if (label === 'RISK_ON')  return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/50';
  if (label === 'RISK_OFF') return 'bg-rose-500/15 text-rose-300 border-rose-500/50';
  return 'bg-amber-500/15 text-amber-300 border-amber-500/50';
};

const RegimeIcon = ({ label }) => {
  if (label === 'RISK_ON')  return <TrendingUp   className="w-4 h-4" />;
  if (label === 'RISK_OFF') return <TrendingDown className="w-4 h-4" />;
  return <Minus className="w-4 h-4" />;
};

// ─── Macro Regime Card ────────────────────────────────────────────
const RegimeBanner = ({ regime, onRefresh, refreshing }) => {
  if (!regime) {
    return (
      <div className="border border-border/40 rounded p-3 text-[12px] text-muted-foreground">
        Macro regime not computed yet. Click "Refresh regime" to compute it.
      </div>
    );
  }
  const { label, score, narrative, signals = [], computed_at } = regime;
  return (
    <div className={`border rounded p-4 ${regimeTone(label)}`} data-testid="swing-regime-banner">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <RegimeIcon label={label} />
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-70">Macro Regime</div>
            <div className="font-semibold text-lg leading-tight">{label.replace('_', ' ')}</div>
          </div>
          <div className="border-l border-current/30 pl-3 ml-1">
            <div className="text-[10px] uppercase tracking-wider opacity-70">Composite score</div>
            <div className="font-mono text-lg font-semibold">
              {(score >= 0 ? '+' : '') + Number(score).toFixed(2)}
            </div>
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="text-[11px] px-3 py-1.5 border border-current/40 rounded hover:bg-black/10 flex items-center gap-1.5 disabled:opacity-50"
          data-testid="regime-refresh-btn"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh regime
        </button>
      </div>
      <p className="text-[12px] mt-2 opacity-90">{narrative}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mt-3 pt-3 border-t border-current/20">
        {signals.map((s) => (
          <div key={s.name} className="text-[10px]">
            <div className="uppercase tracking-wider opacity-60">{s.name.replace(/_/g, ' ')}</div>
            <div className={
              'font-mono font-semibold ' +
              (s.contribution > 0.3 ? 'text-emerald-300' :
               s.contribution < -0.3 ? 'text-rose-300' : 'opacity-70')
            }>
              {s.contribution >= 0 ? '+' : ''}{Number(s.contribution).toFixed(2)}
            </div>
          </div>
        ))}
      </div>
      {computed_at && (
        <div className="text-[10px] opacity-50 mt-2">
          Last computed: {new Date(computed_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};

// ─── Universe Table ───────────────────────────────────────────────
const UniverseTable = ({ universe, bucketFilter, sortBy }) => {
  const rows = useMemo(() => {
    let r = universe;
    if (bucketFilter !== 'ALL') r = r.filter((u) => u.bucket === bucketFilter);
    const cmp = {
      composite: (a, b) => (b.composite_score || 0) - (a.composite_score || 0),
      liquidity: (a, b) => (b.liquidity_score || 0) - (a.liquidity_score || 0),
      volatility: (a, b) => (b.volatility_score || 0) - (a.volatility_score || 0),
      trend: (a, b) => (b.trend_quality_score || 0) - (a.trend_quality_score || 0),
      mcap: (a, b) => (b.mcap_cr || 0) - (a.mcap_cr || 0),
    }[sortBy] || cmp?.composite;
    return [...r].sort(cmp);
  }, [universe, bucketFilter, sortBy]);

  return (
    <div className="border border-border/40 rounded overflow-hidden">
      <table className="w-full text-[12px]">
        <thead className="bg-muted/30 text-muted-foreground uppercase tracking-wider">
          <tr>
            <th className="w-12 text-center px-2 py-2">#</th>
            <th className="text-left px-3 py-2">Symbol</th>
            <th className="text-center px-3 py-2">Bucket</th>
            <th className="text-left px-3 py-2 hidden md:table-cell">Sector</th>
            <th className="text-right px-3 py-2 hidden md:table-cell">Mcap ₹Cr</th>
            <th className="text-right px-3 py-2">Turnover ₹Cr/d</th>
            <th className="text-right px-3 py-2">ATR %</th>
            <th className="text-right px-3 py-2 hidden lg:table-cell">3m ret</th>
            <th className="text-right px-3 py-2 font-semibold">Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={9} className="text-center py-8 text-zinc-500">
                No stocks match this filter.
              </td>
            </tr>
          )}
          {rows.map((u, i) => (
            <tr
              key={u.symbol}
              className="border-t border-border/30 hover:bg-muted/20"
              data-testid={`swing-universe-row-${u.symbol}`}
            >
              <td className="text-center px-2 py-1.5 text-zinc-500 font-mono text-[10px]">
                {i + 1}
              </td>
              <td className="px-3 py-1.5 font-mono font-semibold text-foreground">
                {u.symbol}
              </td>
              <td className="px-3 py-1.5 text-center">
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${BUCKET_TONE[u.bucket] || BUCKET_TONE.FILLER}`}>
                  {u.bucket}
                </span>
              </td>
              <td className="px-3 py-1.5 hidden md:table-cell text-zinc-400 text-[10px]">
                {u.sector || '—'}
              </td>
              <td className="px-3 py-1.5 text-right font-mono text-zinc-400 hidden md:table-cell">
                {u.mcap_cr ? Math.round(u.mcap_cr).toLocaleString('en-IN') : '—'}
              </td>
              <td className="px-3 py-1.5 text-right font-mono text-zinc-300">
                {u.avg_turnover_cr_20d?.toFixed(0) ?? '—'}
              </td>
              <td className={
                'px-3 py-1.5 text-right font-mono ' +
                (u.atr_pct_20d >= 3.5 && u.atr_pct_20d <= 5 ? 'text-emerald-300' :
                 u.atr_pct_20d >= 2.5 ? 'text-zinc-200' : 'text-zinc-500')
              }>
                {u.atr_pct_20d?.toFixed(2) ?? '—'}%
              </td>
              <td className={
                'px-3 py-1.5 text-right font-mono hidden lg:table-cell ' +
                (u.return_3m_pct >= 0 ? 'text-emerald-300' : 'text-rose-300')
              }>
                {u.return_3m_pct != null ? (u.return_3m_pct >= 0 ? '+' : '') + u.return_3m_pct.toFixed(1) + '%' : '—'}
              </td>
              <td className="px-3 py-1.5 text-right font-mono text-sky-300 font-semibold">
                {u.composite_score?.toFixed(1)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Smart-Money Delivery Divergence ──────────────────────────────
const TIER_TONE = {
  EXTREME:  'bg-amber-500/20 text-amber-200 border-amber-400/60',
  STRONG:   'bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/50',
  MODERATE: 'bg-sky-500/10 text-sky-200 border-sky-500/40',
};

// ─── Options Snapshot Status (Wave A of Phase 2) ─────────────────
const OptionsStatusCard = ({ status, loading, onRefresh, onSweep, sweeping }) => {
  const cached = status?.options_snapshots_total || 0;
  const latest = status?.latest_date_cached || '—';
  const todayCount = status?.symbols_on_latest_date || 0;
  const fno = status?.fno_underlyings_indexed || 0;
  return (
    <div className="border border-border/40 rounded p-4 space-y-2" data-testid="options-status-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-fuchsia-300">◈</span>
          <h2 className="font-semibold text-foreground">Options Chain — Wave A</h2>
          <span className="text-[10px] text-zinc-500 italic">
            Daily 17:30 IST EOD sweep · Dhan Options API
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-[11px] px-3 py-1.5 border border-border/50 rounded hover:bg-muted/30 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="options-refresh-status-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={onSweep}
            disabled={sweeping}
            className="text-[11px] px-3 py-1.5 border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200 rounded hover:bg-fuchsia-500/20 disabled:opacity-50"
            data-testid="options-sweep-btn"
          >
            {sweeping ? 'Sweeping…' : 'Run sweep now'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        <div className="border border-border/30 rounded p-2">
          <div className="text-zinc-500 text-[10px] uppercase">Snapshots cached</div>
          <div className="text-lg font-semibold text-foreground">{cached.toLocaleString()}</div>
        </div>
        <div className="border border-border/30 rounded p-2">
          <div className="text-zinc-500 text-[10px] uppercase">Latest date</div>
          <div className="text-lg font-semibold text-foreground">{latest}</div>
        </div>
        <div className="border border-border/30 rounded p-2">
          <div className="text-zinc-500 text-[10px] uppercase">Symbols on latest date</div>
          <div className="text-lg font-semibold text-foreground">{todayCount}</div>
        </div>
        <div className="border border-border/30 rounded p-2">
          <div className="text-zinc-500 text-[10px] uppercase">F&O underlyings mapped</div>
          <div className="text-lg font-semibold text-foreground">{fno}</div>
        </div>
      </div>
      <p className="text-[10px] text-zinc-500 italic leading-relaxed">
        Wave A collects raw option chain snapshots daily. Once we have 30 days of history,
        Wave B unlocks the ★★★ ULTIMATE PANIC BUY composite (PCR · ΔOI · Max Pain · Basis · IV Rank).
      </p>
    </div>
  );
};


const Sparkline = ({ values, width = 60, height = 18 }) => {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  const points = values.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  // Last point highlighted
  const lastIdx = values.length - 1;
  const lx = lastIdx * step;
  const ly = height - ((values[lastIdx] - min) / range) * height;
  return (
    <svg width={width} height={height} className="inline-block align-middle">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1" opacity="0.7" />
      <circle cx={lx} cy={ly} r="1.6" fill="currentColor" />
    </svg>
  );
};

const DivergencePanel = ({ data, loading, onRefresh }) => {
  const [tierFilter, setTierFilter] = useState('ALL');
  const [pullbackOnly, setPullbackOnly] = useState(false);

  const fires = data?.fires || [];
  const filtered = useMemo(() => {
    let f = fires;
    if (tierFilter !== 'ALL') f = f.filter((x) => x.tier === tierFilter);
    if (pullbackOnly) f = f.filter((x) => x.smart_money_pullback || x.has_active_pullback);
    return f;
  }, [fires, tierFilter, pullbackOnly]);

  const tierCounts = useMemo(() => {
    const c = { EXTREME: 0, STRONG: 0, MODERATE: 0 };
    for (const f of fires) if (c[f.tier] != null) c[f.tier]++;
    return c;
  }, [fires]);

  const elite = fires.filter((f) => f.smart_money_pullback);

  return (
    <div className="border border-border/40 rounded p-4 space-y-3" data-testid="smart-money-divergence-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-amber-300">★</span>
          <h2 className="font-semibold text-foreground">Smart-Money Delivery Divergence</h2>
          <span className="text-[11px] text-zinc-500">
            {data?.date ? `as of ${data.date}` : 'no data'} ·
            {' '}E {tierCounts.EXTREME} · S {tierCounts.STRONG} · M {tierCounts.MODERATE}
          </span>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="text-[11px] px-3 py-1.5 border border-border/50 rounded hover:bg-muted/30 flex items-center gap-1.5 disabled:opacity-50"
          data-testid="divergence-refresh-btn"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <p className="text-[11px] text-zinc-500 italic leading-relaxed">
        Symbols where today's <b>delivery %</b> spiked significantly above their 20-day baseline —
        classic institutional accumulation footprint. Higher tier = higher z-score AND higher absolute
        delivery %. Cross-tagged with active PULLBACK setups to surface the "elite Panic Buy" signal.
      </p>

      {/* Elite band */}
      {elite.length > 0 && (
        <div className="border-2 border-amber-400/60 bg-gradient-to-br from-amber-500/10 to-rose-500/10 rounded p-3 space-y-2" data-testid="elite-band">
          <div className="flex items-center gap-2">
            <span className="text-amber-300 font-bold">★ ELITE — PULLBACK + Smart-Money Confirmed</span>
            <span className="text-[10px] text-amber-200/80 italic">
              Both signals in the same stock — the highest-conviction cell.
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {elite.map((f) => (
              <div
                key={`elite-${f.symbol}`}
                className="border border-amber-400/50 bg-black/30 rounded p-2"
                data-testid={`elite-card-${f.symbol}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-amber-100 font-bold">{f.symbol}</span>
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${TIER_TONE[f.tier]}`}>
                    {f.tier}
                  </span>
                </div>
                <div className="text-[10px] text-zinc-300 mt-1">
                  Deliv {f.deliv_pct}% vs {f.baseline_mean_deliv_pct}% avg (z={f.z_score})
                </div>
                <div className="text-[10px] text-zinc-400">
                  ₹{f.deliv_value_cr} Cr delivered @ ₹{f.close}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <span className="text-zinc-500">Tier:</span>
        {['ALL', 'EXTREME', 'STRONG', 'MODERATE'].map((t) => (
          <button
            key={t}
            onClick={() => setTierFilter(t)}
            className={
              'px-2 py-0.5 rounded border ' +
              (tierFilter === t
                ? 'bg-amber-500/20 text-amber-200 border-amber-400/60'
                : 'bg-transparent text-zinc-400 border-border/40 hover:bg-muted/30')
            }
            data-testid={`divergence-tier-filter-${t.toLowerCase()}`}
          >
            {t}{t !== 'ALL' && ` (${tierCounts[t] || 0})`}
          </button>
        ))}
        <span className="text-zinc-600 mx-1">|</span>
        <label className="flex items-center gap-1 cursor-pointer text-zinc-400">
          <input
            type="checkbox"
            checked={pullbackOnly}
            onChange={(e) => setPullbackOnly(e.target.checked)}
            className="accent-amber-400"
            data-testid="divergence-pullback-only-toggle"
          />
          Cross-tagged with PULLBACK only
        </label>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-[12px] text-zinc-500 italic py-3">
          No divergence fires match the current filters.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-[11px]" data-testid="divergence-table">
            <thead className="text-zinc-500">
              <tr className="border-b border-border/40">
                <th className="text-left px-2 py-1.5 font-semibold">Symbol</th>
                <th className="text-left px-2 py-1.5 font-semibold">Tier</th>
                <th className="text-right px-2 py-1.5 font-semibold">Deliv %</th>
                <th className="text-right px-2 py-1.5 font-semibold">20-DMA</th>
                <th className="text-right px-2 py-1.5 font-semibold">Z</th>
                <th className="text-right px-2 py-1.5 font-semibold">₹ Cr Deliv</th>
                <th className="text-right px-2 py-1.5 font-semibold">Close</th>
                <th className="text-center px-2 py-1.5 font-semibold">20-day trend</th>
                <th className="text-center px-2 py-1.5 font-semibold">Cross-tag</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 100).map((f) => (
                <tr
                  key={f.symbol}
                  className={
                    'border-b border-border/20 hover:bg-muted/20 ' +
                    (f.smart_money_pullback ? 'bg-amber-500/5' : '')
                  }
                  data-testid={`divergence-row-${f.symbol}`}
                >
                  <td className="px-2 py-1 font-mono font-semibold text-foreground">{f.symbol}</td>
                  <td className="px-2 py-1">
                    <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${TIER_TONE[f.tier]}`}>
                      {f.tier}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right font-mono text-emerald-300">{f.deliv_pct.toFixed(1)}%</td>
                  <td className="px-2 py-1 text-right font-mono text-zinc-500">{f.baseline_mean_deliv_pct.toFixed(1)}%</td>
                  <td className="px-2 py-1 text-right font-mono text-amber-300">{f.z_score.toFixed(2)}</td>
                  <td className="px-2 py-1 text-right font-mono text-sky-300">{f.deliv_value_cr.toFixed(1)}</td>
                  <td className="px-2 py-1 text-right font-mono text-zinc-300">₹{f.close.toFixed(2)}</td>
                  <td className="px-2 py-1 text-center text-emerald-400">
                    <Sparkline values={f.sparkline_deliv_pct} />
                  </td>
                  <td className="px-2 py-1 text-center">
                    {f.smart_money_pullback ? (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/25 text-amber-100 border border-amber-400/60 font-semibold">
                        ★ ELITE
                      </span>
                    ) : f.has_active_pullback ? (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-200 border border-sky-500/40">
                        + PULLBACK
                      </span>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Options Signal Verdict Chip ─────────────────────────────────
const VERDICT_TONE = {
  bullish: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  bearish: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
  neutral: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  unknown: 'bg-zinc-800/30 text-zinc-600 border-zinc-700/40',
  na:      'bg-zinc-800/20 text-zinc-700 border-zinc-800/30',
};

const OPTIONS_TIER_TONE = {
  '★★★ ULTIMATE': 'bg-amber-400/30 text-amber-100 border-amber-400/70',
  '★★ ELITE':     'bg-amber-500/20 text-amber-200 border-amber-400/60',
  '★ CONFIRMED':  'bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/50',
  RAW:            'bg-zinc-500/10 text-zinc-500 border-zinc-500/30',
  NO_DATA:        'bg-zinc-800/20 text-zinc-700 border-zinc-800/40',
};

const SIGNAL_LABEL = {
  pcr: 'PCR', delta_oi: 'ΔOI', max_pain: 'MP',
  iv_rank: 'IVR', basis: 'BAS',
};

const VerdictChip = ({ label, verdict }) => (
  <span
    className={`text-[9px] uppercase px-1 py-0.5 rounded border ${VERDICT_TONE[verdict] || VERDICT_TONE.unknown}`}
    title={`${label}: ${verdict}`}
  >
    {label}
  </span>
);

// ─── Options Confirmation Panel ──────────────────────────────────
const OptionsConfirmationPanel = ({ data, loading, onRefresh, onCompute, computing }) => {
  const [tierFilter, setTierFilter] = useState('ALL');
  const signals = data?.signals || [];

  const tierCounts = useMemo(() => {
    const c = { '★★★ ULTIMATE': 0, '★★ ELITE': 0, '★ CONFIRMED': 0, RAW: 0 };
    for (const s of signals) {
      const t = s.composite?.tier || 'RAW';
      c[t] = (c[t] || 0) + 1;
    }
    return c;
  }, [signals]);

  const filtered = useMemo(() => {
    if (tierFilter === 'ALL') return signals;
    return signals.filter((s) => s.composite?.tier === tierFilter);
  }, [signals, tierFilter]);

  return (
    <div className="border border-border/40 rounded p-4 space-y-3" data-testid="options-signals-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-fuchsia-300">◈</span>
          <h2 className="font-semibold text-foreground">Options Confirmation — Wave B</h2>
          <span className="text-[11px] text-zinc-500">
            {data?.date ? `as of ${data.date}` : 'no data'} · {signals.length} names ·
            {' '}⭐⭐⭐ {tierCounts['★★★ ULTIMATE']} ·
            {' '}⭐⭐ {tierCounts['★★ ELITE']} ·
            {' '}⭐ {tierCounts['★ CONFIRMED']} ·
            {' '}RAW {tierCounts.RAW}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-[11px] px-3 py-1.5 border border-border/50 rounded hover:bg-muted/30 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="options-signals-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={onCompute}
            disabled={computing}
            className="text-[11px] px-3 py-1.5 border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200 rounded hover:bg-fuchsia-500/20 disabled:opacity-50"
            data-testid="options-signals-compute-btn"
          >
            {computing ? 'Computing…' : 'Recompute'}
          </button>
        </div>
      </div>

      <p className="text-[11px] text-zinc-500 italic leading-relaxed">
        Five-signal composite (PCR · ΔOI · Max Pain · Basis · IV Rank) computed from Dhan option chains.
        Bullish alignment produces ★ CONFIRMED → ★★★ ULTIMATE tiers. IV Rank shows "unknown" until 30-day baseline is built (~Aug 2026).
      </p>

      {/* Tier filters */}
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <span className="text-zinc-500">Tier:</span>
        {['ALL', '★★★ ULTIMATE', '★★ ELITE', '★ CONFIRMED', 'RAW'].map((t) => (
          <button
            key={t}
            onClick={() => setTierFilter(t)}
            className={
              'px-2 py-0.5 rounded border ' +
              (tierFilter === t
                ? 'bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/60'
                : 'bg-transparent text-zinc-400 border-border/40 hover:bg-muted/30')
            }
            data-testid={`options-tier-filter-${t.replace(/\s+/g, '-')}`}
          >
            {t}{t !== 'ALL' && ` (${tierCounts[t] || 0})`}
          </button>
        ))}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-[12px] text-zinc-500 italic py-3">
          No signals for the current filter.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-[11px]" data-testid="options-signals-table">
            <thead className="text-zinc-500">
              <tr className="border-b border-border/40">
                <th className="text-left px-2 py-1.5 font-semibold">Symbol</th>
                <th className="text-left px-2 py-1.5 font-semibold">Tier</th>
                <th className="text-right px-2 py-1.5 font-semibold">Bull/Tot</th>
                <th className="text-right px-2 py-1.5 font-semibold">Spot</th>
                <th className="text-right px-2 py-1.5 font-semibold">PCR</th>
                <th className="text-right px-2 py-1.5 font-semibold">ΔOI CE/PE</th>
                <th className="text-right px-2 py-1.5 font-semibold">Max Pain</th>
                <th className="text-right px-2 py-1.5 font-semibold">Basis %</th>
                <th className="text-right px-2 py-1.5 font-semibold">ATM IV</th>
                <th className="text-center px-2 py-1.5 font-semibold">Verdicts</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 100).map((s) => {
                const c = s.composite || {};
                const v = c.verdicts || {};
                const d = s.delta_oi_atm || {};
                return (
                  <tr key={s.symbol} className="border-b border-border/20 hover:bg-muted/20"
                      data-testid={`options-signal-row-${s.symbol}`}>
                    <td className="px-2 py-1 font-mono font-semibold text-foreground">{s.symbol}</td>
                    <td className="px-2 py-1">
                      <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${OPTIONS_TIER_TONE[c.tier] || OPTIONS_TIER_TONE.RAW}`}>
                        {c.tier || 'RAW'}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right font-mono text-zinc-300">
                      {c.bullish_count ?? '—'}/{c.applicable_count ?? '—'}
                    </td>
                    <td className="px-2 py-1 text-right font-mono text-zinc-400">₹{s.spot?.toFixed?.(2) ?? '—'}</td>
                    <td className={`px-2 py-1 text-right font-mono ${v.pcr === 'bullish' ? 'text-emerald-300' : v.pcr === 'bearish' ? 'text-rose-300' : 'text-zinc-400'}`}>
                      {s.pcr?.toFixed?.(2) ?? '—'}
                    </td>
                    <td className={`px-2 py-1 text-right font-mono ${v.delta_oi === 'bullish' ? 'text-emerald-300' : v.delta_oi === 'bearish' ? 'text-rose-300' : 'text-zinc-400'}`}>
                      {d.ce_pct != null ? `${d.ce_pct > 0 ? '+' : ''}${d.ce_pct}` : '—'}%
                      <span className="text-zinc-600">/</span>
                      {d.pe_pct != null ? `${d.pe_pct > 0 ? '+' : ''}${d.pe_pct}` : '—'}%
                    </td>
                    <td className={`px-2 py-1 text-right font-mono ${v.max_pain === 'bullish' ? 'text-emerald-300' : v.max_pain === 'bearish' ? 'text-rose-300' : 'text-zinc-400'}`}>
                      {s.max_pain ?? '—'}
                      {s.max_pain_offset_pct != null && (
                        <span className="text-zinc-600 ml-1">({s.max_pain_offset_pct > 0 ? '+' : ''}{s.max_pain_offset_pct}%)</span>
                      )}
                    </td>
                    <td className={`px-2 py-1 text-right font-mono ${v.basis === 'bullish' ? 'text-emerald-300' : v.basis === 'bearish' ? 'text-rose-300' : 'text-zinc-400'}`}>
                      {s.basis_pct != null ? `${s.basis_pct > 0 ? '+' : ''}${s.basis_pct}` : '—'}
                    </td>
                    <td className="px-2 py-1 text-right font-mono text-zinc-400">
                      {s.atm_iv?.toFixed?.(1) ?? '—'}
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1 justify-center">
                        {Object.entries(SIGNAL_LABEL).map(([k, lbl]) => (
                          <VerdictChip key={k} label={lbl} verdict={v[k]} />
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Setup Fires ──────────────────────────────────────────────────
const SetupsPanel = ({ setups, onScan, scanning }) => {
  const panic = useMemo(() => setups.filter((s) => s.panic_buy), [setups]);
  const groups = useMemo(() => {
    const g = { BREAKOUT: [], PULLBACK: [], VCP: [] };
    for (const s of setups) if (g[s.type]) g[s.type].push(s);
    return g;
  }, [setups]);
  return (
    <div className="space-y-3" data-testid="swing-setups-panel">
      {/* ─── PANIC BUY hero band ─── */}
      {panic.length > 0 && (
        <div
          className="border-2 border-rose-500/60 bg-gradient-to-br from-rose-500/10 to-fuchsia-500/10 rounded p-4 space-y-2 shadow-lg shadow-rose-500/10"
          data-testid="panic-buy-band"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">🔥</span>
            <h2 className="font-bold text-rose-200 text-[15px] uppercase tracking-wider">
              Panic Buy — {panic.length} fire{panic.length > 1 ? 's' : ''}
            </h2>
            <span className="text-[10px] text-rose-300/80 italic">
              PULLBACK × RISK_OFF · historically strongest cell (58.8% hit · +2.60% avg over 5y)
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {panic.map((s, i) => (
              <div
                key={`panic-${i}`}
                className={
                  'border rounded p-2 bg-black/30 ' +
                  (s.smart_money_confirmed
                    ? 'border-amber-400/70 ring-1 ring-amber-400/40'
                    : 'border-rose-500/40')
                }
                data-testid={`panic-buy-card-${s.symbol}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-rose-100 font-bold text-[13px]">{s.symbol}</span>
                  <span className={
                    'text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold ' +
                    (s.smart_money_confirmed
                      ? 'bg-amber-400/25 text-amber-100 border border-amber-400/70'
                      : 'bg-rose-500/30 text-rose-100 border border-rose-500/60')
                  }>
                    {s.smart_money_confirmed ? '★ ELITE' : '🔥 PANIC BUY'}
                  </span>
                </div>
                <div className="text-[10px] text-zinc-300 mt-1">
                  Conf {(s.confidence * 100).toFixed(0)}% · RSI {s.rsi_14} · Close ₹{s.close}
                </div>
                <div className="text-[10px] text-zinc-400">
                  {s.sector} · {s.bucket} · Regime {s.regime_at_scan}
                </div>
                {s.smart_money_confirmed && (
                  <div className="text-[10px] text-amber-200 mt-1 border-t border-amber-400/30 pt-1">
                    ★ Smart-Money: {s.smart_money_tier} tier · Deliv {s.smart_money_deliv_pct}% (z={s.smart_money_z}) · ₹{s.smart_money_deliv_cr} Cr taken delivery
                  </div>
                )}
                {s.options_tier && s.options_tier !== 'NO_DATA' && s.options_tier !== 'RAW' && (
                  <div className="text-[10px] text-fuchsia-200 mt-1 border-t border-fuchsia-400/30 pt-1">
                    ◈ Options: {s.options_tier} ({s.options_bullish}/{s.options_applicable} bullish)
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Standard fires panel ─── */}
      <div className="border border-border/40 rounded p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            <h2 className="font-semibold text-foreground">Setup Fires</h2>
            <span className="text-[11px] text-zinc-500">
              (BREAKOUT: {groups.BREAKOUT.length} · PULLBACK: {groups.PULLBACK.length} · VCP: {groups.VCP.length})
            </span>
          </div>
          <button
            onClick={onScan}
            disabled={scanning}
            className="text-[11px] px-3 py-1.5 border border-border/50 rounded hover:bg-muted/30 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="setups-scan-btn"
          >
            <RefreshCw className={`w-3 h-3 ${scanning ? 'animate-spin' : ''}`} />
            Rescan
          </button>
        </div>
        {setups.length === 0 && (
          <div className="text-[12px] text-zinc-500 italic">
            No fires yet. Click "Rescan" to run detectors on the current universe
            (this uses cached daily bars — no LLM cost).
          </div>
        )}
        {setups.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {setups.slice(0, 24).map((s, i) => (
              <div
                key={i}
                className={
                  'border rounded p-2 bg-background/40 ' +
                  (s.smart_money_confirmed
                    ? 'border-amber-400/50 ring-1 ring-amber-400/30'
                    : s.panic_buy ? 'border-rose-500/40' : 'border-border/40')
                }
                data-testid={`swing-setup-card-${s.symbol}-${s.type}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-foreground font-semibold">{s.symbol}</span>
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${SETUP_TONE[s.type]}`}>
                    {s.panic_buy ? '🔥 ' : ''}{s.type}
                  </span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  Conf {(s.confidence * 100).toFixed(0)}%
                  {s.volume_ratio && ` · Vol ${s.volume_ratio.toFixed(1)}×`}
                  {s.rsi_14 && ` · RSI ${s.rsi_14}`}
                  {s.last3_range_pct != null && ` · Base ${s.last3_range_pct.toFixed(1)}%`}
                </div>
                <div className="text-[10px] text-zinc-400 mt-0.5">
                  Close ₹{s.close} · {s.sector} · {s.bucket}
                </div>
                {s.smart_money_confirmed && (
                  <div className="text-[10px] text-amber-300 mt-1 flex items-center gap-1">
                    <span>★</span>
                    <span>Smart-Money {s.smart_money_tier} · deliv {s.smart_money_deliv_pct}% (z={s.smart_money_z})</span>
                  </div>
                )}
                {s.options_tier && s.options_tier !== 'NO_DATA' && s.options_tier !== 'RAW' && (
                  <div className="text-[10px] text-fuchsia-300 mt-0.5 flex items-center gap-1">
                    <span>◈</span>
                    <span>Options {s.options_tier} ({s.options_bullish}/{s.options_applicable})</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Backtest Panel ───────────────────────────────────────────────
const BacktestPanel = ({ backtest, onRun, running }) => {
  if (!backtest || backtest.status === 'no_runs_yet') {
    return (
      <div className="border border-border/40 rounded p-4 space-y-2" data-testid="swing-backtest-panel-empty">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-sky-400" />
            <h2 className="font-semibold text-foreground">5-Year Backtest</h2>
          </div>
          <button
            onClick={onRun}
            disabled={running}
            className="text-[11px] px-3 py-1.5 border border-border/50 rounded hover:bg-muted/30 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="backtest-run-btn"
          >
            <RefreshCw className={`w-3 h-3 ${running ? 'animate-spin' : ''}`} />
            Run backtest
          </button>
        </div>
        <p className="text-[12px] text-zinc-500 italic">
          No backtest yet. Click <em>Run backtest</em> to validate the setup detectors on 5 years of history (~60-90s for 100 symbols, no LLM cost).
        </p>
      </div>
    );
  }
  const { overall = {}, by_setup = {}, by_regime = {}, by_setup_regime = {} } = backtest;
  const cell = (v) => (v && v.trades) ? (
    <>
      <div className="font-mono text-foreground">{v.hit_rate_pct?.toFixed(1)}%</div>
      <div className={`text-[10px] ${v.avg_return_pct >= 0 ? 'text-emerald-300' : 'text-rose-300'} font-mono`}>
        {v.avg_return_pct >= 0 ? '+' : ''}{v.avg_return_pct?.toFixed(2)}%
      </div>
      <div className="text-[9px] text-zinc-500">n={v.trades}</div>
    </>
  ) : <div className="text-zinc-600 text-[10px]">—</div>;
  return (
    <div className="border border-border/40 rounded p-4 space-y-3" data-testid="swing-backtest-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-sky-400" />
          <h2 className="font-semibold text-foreground">5-Year Backtest</h2>
          <span className="text-[11px] text-zinc-500">
            {overall.trades} trades · target {backtest.params?.target_pct}% · stop {backtest.params?.stop_atr_mul}×ATR · time-stop {backtest.params?.time_stop_days}d · {backtest.computed_at?.slice(0, 16)}
          </span>
        </div>
        <button
          onClick={onRun}
          disabled={running}
          className="text-[11px] px-3 py-1.5 border border-border/50 rounded hover:bg-muted/30 flex items-center gap-1.5 disabled:opacity-50"
          data-testid="backtest-run-btn"
        >
          <RefreshCw className={`w-3 h-3 ${running ? 'animate-spin' : ''}`} />
          Rerun
        </button>
      </div>

      {/* Overall summary */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-[11px]">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Hit rate</div>
          <div className="font-mono text-lg text-foreground font-semibold">{overall.hit_rate_pct}%</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Avg return</div>
          <div className={`font-mono text-lg font-semibold ${overall.avg_return_pct >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
            {overall.avg_return_pct >= 0 ? '+' : ''}{overall.avg_return_pct?.toFixed(2)}%
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Expectancy</div>
          <div className={`font-mono text-lg font-semibold ${overall.expectancy_pct >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
            {overall.expectancy_pct >= 0 ? '+' : ''}{overall.expectancy_pct?.toFixed(2)}%
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Payoff</div>
          <div className="font-mono text-lg text-foreground font-semibold">{overall.payoff_ratio}×</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Avg days held</div>
          <div className="font-mono text-lg text-foreground font-semibold">{overall.avg_days_held}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Target / Stop / Time</div>
          <div className="font-mono text-[13px] text-zinc-300">
            <span className="text-emerald-300">{overall.target_pct}%</span> ·{' '}
            <span className="text-rose-300">{overall.stop_pct}%</span> ·{' '}
            <span className="text-zinc-400">{overall.time_pct}%</span>
          </div>
        </div>
      </div>

      {/* Setup × Regime matrix */}
      <div className="mt-2 border border-border/30 rounded overflow-hidden">
        <table className="w-full text-[11px]">
          <thead className="bg-muted/30 text-muted-foreground uppercase tracking-wider text-[9px]">
            <tr>
              <th className="text-left px-2 py-1.5 font-semibold">Setup ↓ / Regime →</th>
              <th className="text-center px-2 py-1.5 font-semibold text-emerald-400/70">RISK_ON</th>
              <th className="text-center px-2 py-1.5 font-semibold text-amber-400/70">NEUTRAL</th>
              <th className="text-center px-2 py-1.5 font-semibold text-rose-400/70">RISK_OFF</th>
              <th className="text-center px-2 py-1.5 font-semibold">All</th>
            </tr>
          </thead>
          <tbody>
            {['BREAKOUT', 'PULLBACK', 'VCP'].map((setup) => (
              <tr key={setup} className="border-t border-border/30">
                <td className={`px-2 py-1.5 font-semibold ${SETUP_TONE[setup] ? '' : ''}`}>
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${SETUP_TONE[setup]}`}>
                    {setup}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-center">{cell(by_setup_regime[`${setup}|RISK_ON`])}</td>
                <td className="px-2 py-1.5 text-center">{cell(by_setup_regime[`${setup}|NEUTRAL`])}</td>
                <td className="px-2 py-1.5 text-center">{cell(by_setup_regime[`${setup}|RISK_OFF`])}</td>
                <td className="px-2 py-1.5 text-center bg-muted/10">{cell(by_setup[setup])}</td>
              </tr>
            ))}
            <tr className="border-t border-border/30 bg-muted/10">
              <td className="px-2 py-1.5 font-semibold text-foreground">All setups</td>
              <td className="px-2 py-1.5 text-center">{cell(by_regime.RISK_ON)}</td>
              <td className="px-2 py-1.5 text-center">{cell(by_regime.NEUTRAL)}</td>
              <td className="px-2 py-1.5 text-center">{cell(by_regime.RISK_OFF)}</td>
              <td className="px-2 py-1.5 text-center bg-muted/20">{cell(overall)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-zinc-500 italic">
        Each cell shows hit-rate / avg-return / trade count. Numbers are net of {backtest.params?.slippage_pct}% slippage per side.
        Survivorship bias: uses current universe only.
      </p>
    </div>
  );
};

// ─── Main tab ─────────────────────────────────────────────────────
const SwingTradingTab = () => {
  const [regime, setRegime] = useState(null);
  const [regimeRefreshing, setRegimeRefreshing] = useState(false);
  const [universe, setUniverse] = useState([]);
  const [universeMeta, setUniverseMeta] = useState({});
  const [setups, setSetups] = useState([]);
  const [backtest, setBacktest] = useState(null);
  const [backtesting, setBacktesting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [bucketFilter, setBucketFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('composite');
  const [divergence, setDivergence] = useState(null);
  const [divergenceLoading, setDivergenceLoading] = useState(false);
  const [optionsStatus, setOptionsStatus] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsSweeping, setOptionsSweeping] = useState(false);
  const [optionsSignals, setOptionsSignals] = useState(null);
  const [optionsSignalsLoading, setOptionsSignalsLoading] = useState(false);
  const [optionsSignalsComputing, setOptionsSignalsComputing] = useState(false);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async (signal) => {
    setError(null);
    // Cache-bust every fetch — Swing data changes daily and stale
    // browser caches were causing zeroed panels for admins.
    const ts = Date.now();
    const endpoints = [
      { key: 'regime',    url: `${API}/api/admin/swing/regime?_ts=${ts}` },
      { key: 'universe',  url: `${API}/api/admin/swing/universe?_ts=${ts}` },
      { key: 'setups',    url: `${API}/api/admin/swing/setups?days=7&_ts=${ts}` },
      { key: 'backtest',  url: `${API}/api/admin/swing/backtest/latest?_ts=${ts}` },
      { key: 'divergence',url: `${API}/api/admin/swing/divergence?limit=200&_ts=${ts}` },
      { key: 'options',   url: `${API}/api/admin/swing/options/status?_ts=${ts}` },
      { key: 'signals',   url: `${API}/api/admin/swing/options/signals?setup_type=PULLBACK&limit=200&_ts=${ts}` },
    ];
    try {
      const results = await Promise.all(endpoints.map(ep =>
        fetch(ep.url, { headers: authHeader(), cache: 'no-store', signal })
          .then(async r => ({ key: ep.key, ok: r.ok, status: r.status, statusText: r.statusText, body: r.ok ? await r.json() : null }))
          .catch(err => ({ key: ep.key, ok: false, status: 0, statusText: String(err.message || err), body: null }))
      ));
      if (signal && signal.aborted) return;

      // Surface per-endpoint auth/HTTP failures so the user sees WHY panels
      // are empty (401 = expired JWT, 403 = admin required, 500 = backend).
      const failed = results.filter(r => !r.ok);
      if (failed.length) {
        const has401 = failed.some(f => f.status === 401);
        const has403 = failed.some(f => f.status === 403);
        const parts = failed.map(f => `${f.key}: ${f.status || 'network'} ${f.statusText || ''}`.trim());
        const hint = has401
          ? ' — your admin session may have expired. Please re-login.'
          : has403
          ? ' — this account lacks admin privileges for Swing endpoints.'
          : '';
        setError(`Swing data partially unavailable → ${parts.join(' · ')}${hint}`);
      }

      const byKey = Object.fromEntries(results.map(r => [r.key, r.body || {}]));
      const rD = byKey.regime, uD = byKey.universe, sD = byKey.setups;
      const bD = byKey.backtest, dD = byKey.divergence;
      const oD = byKey.options, ogD = byKey.signals;

      setRegime(rD.latest || null);
      setUniverse(uD.universe || []);
      setUniverseMeta({
        last_built_at: uD.last_built_at,
        last_rebuild_id: uD.last_rebuild_id,
        bucket_counts: uD.bucket_counts,
      });
      setSetups(sD.fires || []);
      setBacktest(bD || null);
      setDivergence(dD || null);
      setOptionsStatus(oD || null);
      setOptionsSignals(ogD || null);
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      setError(String(e.message || e));
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    loadAll(ac.signal);
    return () => ac.abort();
  }, [loadAll]);

  const refreshRegime = async () => {
    setRegimeRefreshing(true);
    try {
      const r = await fetch(`${API}/api/admin/swing/regime/refresh`, {
        method: 'POST', headers: authHeader(),
      });
      if (r.ok) setRegime(await r.json());
    } finally { setRegimeRefreshing(false); }
  };

  const rebuildUniverse = async () => {
    if (!window.confirm('Rebuild the Swing Universe now? This can take 5-8 minutes on cold Yahoo data. The current universe stays live until the new one lands.')) return;
    setRebuilding(true);
    try {
      const r = await fetch(`${API}/api/admin/swing/universe/rebuild?force=true`, {
        method: 'POST', headers: authHeader(),
      });
      if (r.ok) await loadAll();
    } finally { setRebuilding(false); }
  };

  const scanSetups = async () => {
    setScanning(true);
    try {
      const r = await fetch(`${API}/api/admin/swing/setups/scan`, {
        method: 'POST', headers: authHeader(),
      });
      if (r.ok) {
        const s = await fetch(`${API}/api/admin/swing/setups?days=7`, { headers: authHeader() });
        if (s.ok) {
          const sD = await s.json();
          setSetups(sD.fires || []);
        }
      }
    } finally { setScanning(false); }
  };

  const runBacktest = async () => {
    if (!window.confirm('Run 5-year backtest on all 100 names? Takes ~60-90 seconds. Free (no LLM).')) return;
    setBacktesting(true);
    try {
      const r = await fetch(`${API}/api/admin/swing/backtest/run?years=5&min_confidence=0.5`, {
        method: 'POST', headers: authHeader(),
      });
      if (r.ok) setBacktest(await r.json());
    } finally { setBacktesting(false); }
  };

  const refreshDivergence = async () => {
    setDivergenceLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/swing/divergence?limit=200&_ts=${Date.now()}`, { headers: authHeader(), cache: 'no-store' });
      if (r.ok) setDivergence(await r.json());
    } finally { setDivergenceLoading(false); }
  };

  const refreshOptionsStatus = async () => {
    setOptionsLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/swing/options/status?_ts=${Date.now()}`, { headers: authHeader(), cache: 'no-store' });
      if (r.ok) setOptionsStatus(await r.json());
    } finally { setOptionsLoading(false); }
  };

  const runOptionsSweep = async () => {
    if (!window.confirm('Fetch option chains for all F&O names in the current Swing Universe? Takes ~5 minutes (rate-limited by Dhan at 3s/call).')) return;
    setOptionsSweeping(true);
    try {
      const r = await fetch(`${API}/api/admin/swing/options/sweep`, {
        method: 'POST', headers: authHeader(),
      });
      if (r.ok) {
        await refreshOptionsStatus();
      }
    } finally { setOptionsSweeping(false); }
  };

  const refreshOptionsSignals = async () => {
    setOptionsSignalsLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/swing/options/signals?setup_type=PULLBACK&limit=200`, {
        headers: authHeader(),
      });
      if (r.ok) setOptionsSignals(await r.json());
    } finally { setOptionsSignalsLoading(false); }
  };

  const computeOptionsSignals = async () => {
    setOptionsSignalsComputing(true);
    try {
      // Compute for both setup types in parallel
      await Promise.all([
        fetch(`${API}/api/admin/swing/options/signals/compute?setup_type=PULLBACK`, {
          method: 'POST', headers: authHeader(),
        }),
        fetch(`${API}/api/admin/swing/options/signals/compute?setup_type=BREAKOUT`, {
          method: 'POST', headers: authHeader(),
        }),
      ]);
      await refreshOptionsSignals();
    } finally { setOptionsSignalsComputing(false); }
  };

  const buckets = useMemo(() => {
    const counts = { ALL: universe.length, CORE: 0, MOMENTUM: 0, SECTOR: 0, EVENT: 0, FILLER: 0 };
    for (const u of universe) if (counts[u.bucket] != null) counts[u.bucket]++;
    return counts;
  }, [universe]);

  return (
    <div className="space-y-4" data-testid="swing-trading-tab">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-sky-400" />
            <h1 className="text-lg font-semibold text-foreground">Swing Trading</h1>
          </div>
          <p className="text-[12px] text-muted-foreground mt-1 max-w-2xl">
            Fixed 100-name watchlist rebuilt monthly, macro regime gate,
            and daily setup detection. Event + Weekly-setup streams share
            the same universe.
          </p>
        </div>
        <button
          onClick={rebuildUniverse}
          disabled={rebuilding}
          className="text-[11px] px-3 py-1.5 border border-border/50 rounded hover:bg-muted/30 flex items-center gap-1.5 disabled:opacity-50"
          data-testid="rebuild-universe-btn"
        >
          <RefreshCw className={`w-3 h-3 ${rebuilding ? 'animate-spin' : ''}`} />
          Rebuild universe
        </button>
      </div>

      {/* ── How-to-read guide (collapsible) ── */}
      <details className="border border-border/30 rounded p-3 text-[11px] text-zinc-400 leading-relaxed" data-testid="swing-help-details">
        <summary className="cursor-pointer text-foreground font-semibold text-[12px] flex items-center gap-2 select-none">
          <BookOpen className="w-3.5 h-3.5 text-sky-400" />
          How to read this tab
        </summary>
        <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <div className="text-foreground font-semibold mb-1">📸 Wave A — Options Chain (raw data)</div>
            <p>Daily EOD snapshot of every F&O option chain — 100 stocks × ~100 strikes each. Just storage; no signals computed here. Feeds Wave B.</p>
            <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
              <li><b>Snapshots cached</b>: total chain-days stored across all time (grows ~100/day).</li>
              <li><b>Latest date</b>: should be today (or yesterday if before 17:30 IST).</li>
              <li><b>Symbols on latest date</b>: target 100. Currently ~34 because 69 universe symbols were dropped from F&O by SEBI.</li>
              <li><b>F&O underlyings mapped</b>: universe of NSE stocks with derivatives (~228 total).</li>
            </ul>
          </div>
          <div>
            <div className="text-foreground font-semibold mb-1">◈ Wave B — 5-Signal Composite</div>
            <p>Each stock scored on 5 institutional signals every EOD. Bullish alignment produces tier badges.</p>
            <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
              <li><b>PCR</b> ≥ 1.3 = retail fear peak (contrarian bullish for pullback).</li>
              <li><b>ΔOI</b> = OI change at ATM ±3 strikes. Calls rising + puts flat = bullish footprint.</li>
              <li><b>Max Pain</b>: spot 1%+ below max pain = upside magnet.</li>
              <li><b>Basis</b>: near-month futures premium ≥ +0.3% = pros bullish.</li>
              <li><b>IV Rank</b> ≥ 70 = fear peaked. Shows "unknown" until ~2026-08-03 (needs 30d baseline).</li>
            </ul>
            <div className="mt-1.5">
              <b>Tier logic:</b> <span className="text-amber-200">⭐⭐⭐ ULTIMATE</span> = 5/5 bullish · <span className="text-amber-200">⭐⭐ ELITE</span> = 4/5 · <span className="text-fuchsia-300">⭐ CONFIRMED</span> = 3/5 · RAW = below.
            </div>
          </div>
          <div>
            <div className="text-foreground font-semibold mb-1">★ Smart-Money Delivery Divergence</div>
            <p>Stocks where today's delivery% spikes ≥1.5σ above their 20-day average — classic institutional accumulation footprint. Cross-tagged with active PULLBACK setups to surface the "elite Panic Buy" signal.</p>
          </div>
          <div>
            <div className="text-foreground font-semibold mb-1">📊 5-Year Backtest</div>
            <p>Historical replay of BREAKOUT/PULLBACK/VCP setup detectors over 5 years of daily bars against the current universe. Each cell reads: <code>hit-rate% / avg-return% / trade-count</code>.</p>
            <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
              <li><b>PULLBACK × RISK_OFF</b> = highest edge cell (58.8% hit, +2.60% avg). Your "Panic Buy" stream.</li>
              <li><b>BREAKOUT × NEUTRAL</b> = 0 trades — regime gate working (breakouts fail in choppy markets).</li>
              <li><b>Payoff</b> {'>'} 1 = winners bigger than losers; <b>Target/Stop/Time</b> = why trades closed.</li>
            </ul>
          </div>
        </div>
        <div className="mt-3 pt-2 border-t border-border/30 text-[10px] italic">
          <b>Tip:</b> if panels look empty despite backend having data, hard-refresh the page (Ctrl+Shift+R or Cmd+Shift+R). This happens when the tab was opened during a backend restart.
        </div>
      </details>

      {/* ── Regime banner ── */}
      <RegimeBanner
        regime={regime}
        onRefresh={refreshRegime}
        refreshing={regimeRefreshing}
      />

      {/* ── Setup fires ── */}
      <SetupsPanel setups={setups} onScan={scanSetups} scanning={scanning} />

      {/* ── Smart-Money Delivery Divergence ── */}
      <DivergencePanel
        data={divergence}
        loading={divergenceLoading}
        onRefresh={refreshDivergence}
      />

      {/* ── Options Chain Status (Wave A of Phase 2) ── */}
      <OptionsStatusCard
        status={optionsStatus}
        loading={optionsLoading}
        onRefresh={refreshOptionsStatus}
        onSweep={runOptionsSweep}
        sweeping={optionsSweeping}
      />

      {/* ── Options Confirmation Signals (Wave B) ── */}
      <OptionsConfirmationPanel
        data={optionsSignals}
        loading={optionsSignalsLoading}
        onRefresh={refreshOptionsSignals}
        onCompute={computeOptionsSignals}
        computing={optionsSignalsComputing}
      />

      {/* ── 5-year Backtest ── */}
      <BacktestPanel backtest={backtest} onRun={runBacktest} running={backtesting} />

      {/* ── Universe controls ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-emerald-400" />
          <h2 className="font-semibold text-foreground">Swing Universe</h2>
          <span className="text-[11px] text-zinc-500">
            {universe.length} names · rebuilt{' '}
            {universeMeta.last_built_at ? new Date(universeMeta.last_built_at).toLocaleDateString() : '—'}
          </span>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex rounded overflow-hidden border border-border/50 text-[11px]">
            {['ALL', 'CORE', 'MOMENTUM', 'SECTOR', 'EVENT', 'FILLER'].map((b) => (
              <button
                key={b}
                onClick={() => setBucketFilter(b)}
                className={
                  'px-2 py-1 border-r border-border/50 last:border-r-0 font-mono ' +
                  (bucketFilter === b
                    ? 'bg-sky-500/20 text-sky-200 font-semibold'
                    : 'text-zinc-400 hover:bg-muted/30')
                }
                data-testid={`bucket-filter-${b}`}
              >
                {b} <span className="text-zinc-500">({buckets[b] || 0})</span>
              </button>
            ))}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-background border border-border/50 rounded px-2 py-1 text-[11px]"
            data-testid="universe-sort-select"
          >
            <option value="composite">Sort: Composite</option>
            <option value="liquidity">Sort: Liquidity</option>
            <option value="volatility">Sort: Volatility</option>
            <option value="trend">Sort: Trend quality</option>
            <option value="mcap">Sort: Market cap</option>
          </select>
        </div>
      </div>

      {error && (
        <div
          data-testid="swing-error-banner"
          className="border border-rose-500/40 bg-rose-500/10 text-rose-300 text-[12px] px-3 py-2 rounded flex items-start justify-between gap-3"
        >
          <div className="flex-1 leading-relaxed">
            <span className="font-semibold text-rose-200">⚠ {error}</span>
          </div>
          <button
            data-testid="swing-error-retry"
            onClick={() => loadAll()}
            className="shrink-0 text-[11px] px-2 py-0.5 border border-rose-400/50 rounded hover:bg-rose-500/20"
          >
            Retry
          </button>
        </div>
      )}

      <UniverseTable universe={universe} bucketFilter={bucketFilter} sortBy={sortBy} />
    </div>
  );
};

export default SwingTradingTab;
